/**
 * GameReview.js
 * Final review step before creating the game
 */

import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Divider,
    Chip,
    Table,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import {
    Person as PersonIcon,
    SmartToy as SmartToyIcon
} from '@mui/icons-material';

export default function GameReview({ gameType, gameName, config, players }) {
    const humanPlayers = players.filter(p => p.is_human).length;
    const aiPlayers = players.filter(p => !p.is_human).length;

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Review Your Game
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Review your game configuration before creating it.
            </Typography>

            <Grid container spacing={3}>
                {/* Game Info */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Game Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell><strong>Game Type</strong></TableCell>
                                    <TableCell>{gameType.name}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>Game Name</strong></TableCell>
                                    <TableCell>{gameName}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>Total Players</strong></TableCell>
                                    <TableCell>{players.length}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>Human Players</strong></TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<PersonIcon />}
                                            label={humanPlayers}
                                            size="small"
                                            color="primary"
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>AI Players</strong></TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<SmartToyIcon />}
                                            label={aiPlayers}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                {/* Game Configuration */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Game Configuration
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Table size="small">
                            <TableBody>
                                {Object.entries(config).map(([key, value]) => (
                                    <TableRow key={key}>
                                        <TableCell>
                                            <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                                        </TableCell>
                                        <TableCell>
                                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                                             typeof value === 'object' ? JSON.stringify(value) :
                                             value}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                {/* Players List */}
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Players
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={2}>
                            {players.map((player, index) => (
                                <Grid item xs={12} sm={6} md={4} key={player.id}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            {player.is_human ? (
                                                <PersonIcon color="primary" />
                                            ) : (
                                                <SmartToyIcon color="action" />
                                            )}
                                            <Typography variant="subtitle1">
                                                {player.name}
                                            </Typography>
                                            <Chip
                                                label={player.is_human ? 'Human' : 'AI'}
                                                size="small"
                                                color={player.is_human ? 'primary' : 'default'}
                                            />
                                        </Box>

                                        {!player.is_human && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Model: {player.model.split('/').pop()}
                                            </Typography>
                                        )}

                                        {player.character_name && player.character_name !== player.name && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Character: {player.character_name}
                                            </Typography>
                                        )}

                                        {player.secret_objective && (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                Has secret objective
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
